import { db } from "@repo/ui";
import { collectionGroup, onSnapshot } from "firebase/firestore";
import {
	Bell,
	Bot,
	BrainCircuit,
	Briefcase,
	Building2,
	CircleDollarSign,
	Code,
	FileText,
	History,
	Inbox,
	LayoutDashboard,
	ListChecks,
	ListTodo,
	Logs,
	MessageCircle,
	Phone,
	Settings,
	ShieldAlert,
	Smartphone,
	UserPlus,
	Users,
	X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "./ui/utils";

interface SidebarProps {
	currentPage: string;
	onNavigate: (page: string) => void;
	isCollapsed: boolean;
	isMobileOpen: boolean;
	isMobileViewport: boolean;
	onMobileClose: () => void;
}

interface MenuItem {
	id: string;
	label: string;
	icon: React.ComponentType<{ className?: string }>;
}

const menuGroups: { label: string; items: MenuItem[] }[] = [
	{
		label: "Workspace",
		items: [
			{ id: "dashboard", label: "Overview", icon: LayoutDashboard },
			{ id: "alerts", label: "Alerts", icon: Bell },
			{ id: "dead-letters", label: "Failed events", icon: Inbox },
			{ id: "admin-tasks", label: "Admin tasks", icon: ListTodo },
			{ id: "pii-review", label: "PII review", icon: ShieldAlert },
		],
	},
	{
		label: "Customers",
		items: [
			{ id: "assisted-onboarding", label: "Onboarding", icon: UserPlus },
			{ id: "companies", label: "Companies", icon: Building2 },
			{ id: "users", label: "Users", icon: Users },
			{ id: "callers", label: "Callers", icon: BrainCircuit },
		],
	},
	{
		label: "Voice platform",
		items: [
			{ id: "calls", label: "Calls", icon: Phone },
			{ id: "agents", label: "Agents", icon: Bot },
			{ id: "services", label: "Services", icon: Briefcase },
			{ id: "playbook-runs", label: "Playbook runs", icon: ListChecks },
			{ id: "phone-numbers", label: "Phone numbers", icon: Smartphone },
			{
				id: "whatsapp-senders",
				label: "WhatsApp senders",
				icon: MessageCircle,
			},
			{
				id: "whatsapp-templates",
				label: "WhatsApp templates",
				icon: MessageCircle,
			},
			{ id: "test-routing", label: "Test routing", icon: Code },
		],
	},
	{
		label: "Diagnostics",
		items: [
			{ id: "cogs", label: "COGS ledger", icon: CircleDollarSign },
			{ id: "events", label: "Raw events", icon: FileText },
			{ id: "tcdrs", label: "T-CDRs", icon: Phone },
			{ id: "audit-findings", label: "Audit findings", icon: ListChecks },
			{ id: "audit-logs", label: "Audit logs", icon: History },
			{ id: "logs", label: "LiveKit logs", icon: Logs },
			{ id: "developer-tests", label: "Developer tests", icon: Code },
		],
	},
	{
		label: "Configuration",
		items: [{ id: "settings", label: "System settings", icon: Settings }],
	},
];

const detailPageParent: Record<string, string> = {
	"company-details": "companies",
	"user-details": "users",
	"caller-details": "callers",
	"agent-details": "agents",
	"service-details": "services",
	"pii-review-editor": "pii-review",
};

export function Sidebar({
	currentPage,
	onNavigate,
	isCollapsed,
	isMobileOpen,
	isMobileViewport,
	onMobileClose,
}: SidebarProps) {
	const [piiOpenCount, setPiiOpenCount] = useState(0);
	useEffect(
		() =>
			onSnapshot(
				collectionGroup(db, "piiReviewQueue"),
				(snapshot) => {
					setPiiOpenCount(
						snapshot.docs.filter((item) => item.data().status !== "resolved")
							.length,
					);
				},
				() => setPiiOpenCount(0),
			),
		[],
	);
	const activePage = detailPageParent[currentPage] ?? currentPage;
	const handleNavigate = (page: string) => {
		onNavigate(page);
		onMobileClose();
	};

	return (
		<>
			{isMobileOpen && (
				<button
					type="button"
					className="admin-sidebar-backdrop"
					aria-label="Close navigation"
					onClick={onMobileClose}
				/>
			)}
			<aside
				id="admin-sidebar-navigation"
				inert={isMobileViewport && !isMobileOpen}
				aria-hidden={isMobileViewport && !isMobileOpen}
				className={cn(
					"admin-sidebar",
					isCollapsed && "admin-sidebar--collapsed",
					isMobileOpen && "admin-sidebar--mobile-open",
				)}
			>
				<div className="admin-sidebar-mobile-header">
					<span>Navigation</span>
					<button
						type="button"
						onClick={onMobileClose}
						aria-label="Close navigation"
					>
						<X className="size-4" />
					</button>
				</div>
				<nav className="admin-sidebar-nav" aria-label="Admin sections">
					{menuGroups.map((group) => (
						<div className="admin-sidebar-group" key={group.label}>
							<div className="admin-sidebar-group-label">{group.label}</div>
							{group.items.map((item) => {
								const Icon = item.icon;
								const isActive = activePage === item.id;
								return (
									<button
										key={item.id}
										type="button"
										onClick={() => handleNavigate(item.id)}
										className={cn(
											"admin-sidebar-link",
											isActive && "is-active",
										)}
										aria-current={isActive ? "page" : undefined}
										aria-label={isCollapsed ? item.label : undefined}
										title={isCollapsed ? item.label : undefined}
									>
										<Icon className="size-[18px] shrink-0" />
										<span>{item.label}</span>
										{item.id === "pii-review" && piiOpenCount > 0 && (
											<span
												aria-label={`${piiOpenCount} open cases`}
												className="ml-auto rounded-full bg-teal-100 px-1.5 text-xs font-semibold text-teal-900"
											>
												{piiOpenCount}
											</span>
										)}
									</button>
								);
							})}
						</div>
					))}
				</nav>
				<div className="admin-sidebar-footer">
					<span className="admin-sidebar-footer-mark" />
					<span>System admin workspace</span>
				</div>
			</aside>
		</>
	);
}
